import { API_BASE, authHeaders } from './apiAuth'
import { decodeRefs, type ImageRef } from './imageRefs'
import type { Market } from './amazonService'
import { searchStock } from './stockService'

/**
 * Google Images, from the page's side (2026-10-14).
 *
 * Everything goes through the Worker (routes/webImages.ts), which holds the
 * Scale SERP key and keeps each answer for a week. The grid shows the
 * pictures straight from the sites that have them, which costs nothing.
 *
 * The country picker is the Amazon one: the same ten countries.
 */

export type Country = Market

export interface FoundImage {
  url: string
  thumb: string
  title: string
  page: string
  site: string
  width?: number
  height?: number
  /** Unsplash: the photographer, to be credited, and their page. */
  author?: string
  authorUrl?: string
  /** Unsplash: called once when the picture is used (their rule). */
  downloadLocation?: string
}

export interface FoundImagePage {
  results: FoundImage[]
  page: number
  more: boolean
  cached: boolean
  /** What Scale SERP says is left on the plan; null when it did not say. */
  creditsLeft: number | null
}

async function reason(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    return body.error || `Request failed (${response.status})`
  } catch {
    return `Request failed (${response.status})`
  }
}

export async function searchWebImages(country: Country, term: string, page = 1): Promise<FoundImagePage> {
  const query = new URLSearchParams({ country, q: term, page: String(page) })
  const response = await fetch(`${API_BASE}/api/web-images/search?${query}`, {
    // Longer than the Worker's own wait for Google (60 s), so the Worker's
    // reason arrives rather than a bare timeout.
    signal: AbortSignal.timeout(95_000),
    headers: { Accept: 'application/json', ...(await authHeaders()) },
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as Partial<FoundImagePage>
  return {
    results: Array.isArray(body.results) ? body.results : [],
    page: Number(body.page) || page,
    more: body.more === true,
    cached: body.cached === true,
    creditsLeft: typeof body.creditsLeft === 'number' ? body.creditsLeft : null,
  }
}

/** One picked picture: where it is, and where it was found. */
export interface PickedImage {
  url: string
  thumb: string
  title: string
  page: string
  site: string
  /** Which library it came from, kept with the picture. Google when missing. */
  source?: 'google' | 'unsplash'
  /** "Photo by … on Unsplash", kept with the picture. */
  credit?: string
  downloadLocation?: string
}

/**
 * Unsplash, through the Mood board's route (routes/stock.ts), in the shape
 * the Google results have, so one page draws both (External Library,
 * 2026-10-14). Thirty a page; free; nothing on the Usage page.
 */
export async function searchUnsplash(term: string, page = 1): Promise<FoundImagePage> {
  const found = await searchStock('unsplash', term, page)
  return {
    results: found.pictures.map((one) => ({
      // Kept at its own size (Huanrui, 2026-10-16: everything sent to the
      // canvas or Miro is the original); 2400 px wide where the Worker is older.
      url: one.full || one.url.replace(/([?&])w=\d+/, '$1w=2400'),
      thumb: one.thumb,
      title: one.title,
      page: one.link,
      site: 'Unsplash',
      width: one.width,
      height: one.height,
      author: one.author,
      authorUrl: one.authorUrl,
      downloadLocation: one.downloadLocation,
    })),
    page,
    more: found.more,
    cached: false,
    creditsLeft: null,
  }
}

const KEEP_BATCH = 40

/** Kept into the personal project, for Miro. */
export async function keepWebImages(
  picked: PickedImage[],
): Promise<{ refs: ImageRef[]; failed: number }> {
  const refs: ImageRef[] = []
  let failed = 0
  for (let start = 0; start < picked.length; start += KEEP_BATCH) {
    const batch = picked.slice(start, start + KEEP_BATCH)
    const response = await fetch(`${API_BASE}/api/web-images/keep`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      signal: AbortSignal.timeout(120_000),
      body: JSON.stringify({ pictures: batch }),
    })
    if (!response.ok) throw new Error(await reason(response))
    const body = (await response.json()) as { refs?: unknown; failed?: unknown[] }
    refs.push(...decodeRefs(body.refs))
    failed += Array.isArray(body.failed) ? body.failed.length : 0
  }
  return { refs, failed }
}

/** Kept straight into one project (Copy to project): file ids in order, null for a failure. */
export async function keepWebIntoProject(projectId: string, picked: PickedImage[]): Promise<(string | null)[]> {
  const out: (string | null)[] = []
  for (let start = 0; start < picked.length; start += KEEP_BATCH) {
    const batch = picked.slice(start, start + KEEP_BATCH)
    const response = await fetch(`${API_BASE}/api/web-images/keep`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      signal: AbortSignal.timeout(120_000),
      body: JSON.stringify({ pictures: batch, project: projectId }),
    })
    if (!response.ok) throw new Error(await reason(response))
    const body = (await response.json()) as { slots?: unknown[] }
    const slots = Array.isArray(body.slots) ? body.slots : []
    batch.forEach((_, index) => {
      const ref = decodeRefs([slots[index]])[0]
      out.push(ref && ref.store === 'project' ? ref.id : null)
    })
  }
  return out
}
